/**
 * Tenant Configuration Editor
 * Task 28: Phase 3 - Frontend Integration
 * 
 * Form for editing tenant deployment configuration.
 * Admin-only component for managing per-tenant cloud resources.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Database, 
  HardDrive, 
  Server, 
  Brain, 
  Save, 
  RefreshCw,
  Check,
  X,
  AlertCircle,
  Cloud,
  Settings2,
} from 'lucide-react';

import { Button } from '../UI/Button';
import { Card } from '../UI/Card';
import { Input } from '../UI/Input';
import { Select } from '../UI/Select';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { 
  getTenantConfig, 
  updateTenantConfig, 
  reloadTenantConfig,
  validateTenantConfig,
  getAzureConfigFromEnv,
} from '../../api/tenant-config';
import type { 
  TenantDeploymentConfig, 
  TenantConfigUpdateRequest,
  TenantConfigValidationResult,
} from '../../types/tenant-config';

interface TenantConfigEditorProps {
  tenantId: string;
  tenantName: string;
  onSaved?: () => void;
}

type DeploymentType = 'shared' | 'dedicated' | 'customer_cloud' | 'hybrid' | 'on_premise';
type CloudProvider = 'azure' | 'aws' | 'gcp' | 'on_premise' | '';

export function TenantConfigEditor({ tenantId, tenantName, onSaved }: TenantConfigEditorProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<TenantConfigUpdateRequest>({});
  const [validationResult, setValidationResult] = useState<TenantConfigValidationResult | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'database' | 'storage' | 'redis' | 'openai' | 'azure'>('general');

  // Load config
  const { data: config, isLoading, error } = useQuery({
    queryKey: ['tenant-config', tenantId],
    queryFn: () => getTenantConfig(tenantId),
    enabled: !!tenantId,
  });

  // Initialize form data when config loads
  useEffect(() => {
    if (config) {
      setFormData({
        deploymentType: config.deploymentType,
        cloudProvider: config.cloudProvider || undefined,
        isActive: config.isActive,
        // Database
        databaseHost: config.database?.host || undefined,
        databasePort: config.database?.port || undefined,
        databaseName: config.database?.database || undefined,
        databaseUser: config.database?.user || undefined,
        databaseSslMode: config.database?.sslMode || undefined,
        databasePoolMinSize: config.database?.poolMinSize || undefined,
        databasePoolMaxSize: config.database?.poolMaxSize || undefined,
        // Storage
        storageProvider: config.storage?.provider || undefined,
        storageAccountName: config.storage?.accountName || undefined,
        // Redis
        redisHost: config.redis?.host || undefined,
        redisPort: config.redis?.port || undefined,
        redisSslEnabled: config.redis?.sslEnabled || undefined,
        // OpenAI
        openaiProvider: config.openai?.provider || undefined,
        openaiEndpoint: config.openai?.endpoint || undefined,
        openaiApiVersion: config.openai?.apiVersion || undefined,
        openaiEmbeddingDeployment: config.openai?.embeddingDeployment || undefined,
        openaiEmbeddingDimension: config.openai?.embeddingDimension || undefined,
        openaiLlmDeployment: config.openai?.llmDeployment || undefined,
        // Azure
        azureSubscriptionId: config.azure?.subscriptionId || undefined,
        azureResourceGroup: config.azure?.resourceGroup || undefined,
        azureRegion: config.azure?.region || undefined,
        azureKeyVaultName: config.azure?.keyVaultName || undefined,
        azureManagedIdentityEnabled: config.azure?.managedIdentityEnabled || undefined,
      });
    }
  }, [config]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => updateTenantConfig(tenantId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantId] });
      onSaved?.();
    },
  });

  // Reload mutation
  const reloadMutation = useMutation({
    mutationFn: () => reloadTenantConfig(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantId] });
    },
  });

  // Validate mutation
  const validateMutation = useMutation({
    mutationFn: () => validateTenantConfig(tenantId, true),
    onSuccess: (result) => {
      setValidationResult(result);
    },
  });

  const handleInputChange = (field: keyof TenantConfigUpdateRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Helper function to load Azure config from environment variables
  const loadAzureFromEnvironment = async () => {
    try {
      // Call backend API to get Azure config from environment variables
      const envConfig = await getAzureConfigFromEnv(tenantId);
      
      return {
        azureSubscriptionId: envConfig.azure_subscription_id || undefined,
        azureResourceGroup: envConfig.azure_resource_group || undefined,
        azureRegion: envConfig.azure_region || undefined,
        azureKeyVaultName: envConfig.azure_key_vault_name || undefined,
        azureManagedIdentityEnabled: envConfig.azure_managed_identity_enabled || false,
      };
    } catch (error) {
      console.error('Failed to load Azure config from environment:', error);
      // Fallback to Vite environment variables if available
      return {
        azureSubscriptionId: import.meta.env.VITE_AZURE_SUBSCRIPTION_ID || undefined,
        azureResourceGroup: import.meta.env.VITE_AZURE_RESOURCE_GROUP || undefined,
        azureRegion: import.meta.env.VITE_AZURE_REGION || import.meta.env.VITE_AZURE_LOCATION || undefined,
        azureKeyVaultName: import.meta.env.VITE_AZURE_KEY_VAULT_NAME || undefined,
        azureManagedIdentityEnabled: import.meta.env.VITE_AZURE_MANAGED_IDENTITY_ENABLED === 'true' || false,
      };
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Settings2 },
    { id: 'database', label: 'Database', icon: Database },
    { id: 'storage', label: 'Storage', icon: HardDrive },
    { id: 'redis', label: 'Redis', icon: Server },
    { id: 'openai', label: 'OpenAI', icon: Brain },
    { id: 'azure', label: 'Azure', icon: Cloud },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
        <span className="ml-2">Loading configuration...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-gray-900 dark:text-gray-100">
        <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load tenant configuration</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {tenantName} Configuration
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {config ? 'Custom configuration' : 'Using core services (no custom config)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => validateMutation.mutate()}
            disabled={validateMutation.isPending}
          >
            {validateMutation.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Check className="w-4 h-4 mr-1" />
            )}
            Validate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => reloadMutation.mutate()}
            disabled={reloadMutation.isPending}
          >
            {reloadMutation.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1" />
            )}
            Reload
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Save className="w-4 h-4 mr-1" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Validation Results */}
      {validationResult && (
        <Card className={`p-4 ${validationResult.isValid ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-gray-900 dark:text-gray-100' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-gray-900 dark:text-gray-100'}`}>
          <div className="flex items-center gap-2 mb-2">
            {validationResult.isValid ? (
              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
            ) : (
              <X className="w-5 h-5 text-red-600 dark:text-red-400" />
            )}
            <span className={`font-medium ${validationResult.isValid ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
              {validationResult.isValid ? 'All connections valid' : 'Validation failed'}
            </span>
          </div>
          {validationResult.errors && validationResult.errors.length > 0 && (
            <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400">
              {validationResult.errors.map((err, idx) => (
                <li key={idx}>{err.field}: {err.message}</li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex gap-4 text-sm">
            {Object.entries(validationResult.resourceHealth || {}).map(([resource, status]) => (
              <span key={resource} className={`flex items-center gap-1 ${
                status === 'healthy' ? 'text-green-600' : 
                status === 'unhealthy' ? 'text-red-600' : 'text-slate-500'
              }`}>
                {status === 'healthy' ? <Check className="w-3 h-3" /> : 
                 status === 'unhealthy' ? <X className="w-3 h-3" /> : null}
                {resource}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <Card className="p-6">
        {activeTab === 'general' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                Deployment Type
              </label>
              <select
                value={formData.deploymentType || 'shared'}
                onChange={(e) => handleInputChange('deploymentType', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="shared">Shared (Core Services)</option>
                <option value="dedicated">Dedicated</option>
                <option value="customer_cloud">Customer Cloud</option>
                <option value="hybrid">Hybrid</option>
                <option value="on_premise">On-Premise</option>
              </select>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Shared: Uses core Sparc/Alpyne services. Customer Cloud: Uses tenant's own Azure resources. On-Premise: Uses customer's own infrastructure.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                Cloud Provider
              </label>
              <select
                value={formData.cloudProvider || ''}
                onChange={(e) => handleInputChange('cloudProvider', e.target.value || null)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">None (Shared)</option>
                <option value="azure">Azure</option>
                <option value="aws">AWS</option>
                <option value="gcp">GCP</option>
                <option value="on_premise">On-Premise</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive ?? true}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="isActive" className="text-sm text-gray-900 dark:text-gray-100">
                Configuration Active
              </label>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                (If disabled, tenant uses core services)
              </span>
            </div>
          </div>
        )}

        {activeTab === 'database' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Host</label>
                <input
                  type="text"
                  value={formData.databaseHost || ''}
                  onChange={(e) => handleInputChange('databaseHost', e.target.value || null)}
                  placeholder="e.g., mydb.postgres.database.azure.com"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Port</label>
                <input
                  type="number"
                  value={formData.databasePort || 5432}
                  onChange={(e) => handleInputChange('databasePort', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Database Name</label>
                <input
                  type="text"
                  value={formData.databaseName || ''}
                  onChange={(e) => handleInputChange('databaseName', e.target.value || null)}
                  placeholder="e.g., traceos"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Username</label>
                <input
                  type="text"
                  value={formData.databaseUser || ''}
                  onChange={(e) => handleInputChange('databaseUser', e.target.value || null)}
                  placeholder="e.g., dbadmin"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Password</label>
                <input
                  type="password"
                  value={formData.databasePassword || ''}
                  onChange={(e) => handleInputChange('databasePassword', e.target.value || null)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Leave blank to keep existing</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">SSL Mode</label>
                <select
                  value={formData.databaseSslMode || 'require'}
                  onChange={(e) => handleInputChange('databaseSslMode', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="require">Require</option>
                  <option value="prefer">Prefer</option>
                  <option value="disable">Disable</option>
                </select>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <strong>Note:</strong> Leave fields empty to use core database (c-traceos-postgres).
            </p>
          </div>
        )}

        {activeTab === 'storage' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Provider</label>
                <select
                  value={formData.storageProvider || 'azure_blob'}
                  onChange={(e) => handleInputChange('storageProvider', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="azure_blob">Azure Blob Storage</option>
                  <option value="aws_s3">AWS S3</option>
                  <option value="gcp_storage">GCP Storage</option>
                  <option value="filesystem">Filesystem</option>
                </select>
              </div>
              {formData.storageProvider === 'filesystem' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Storage Path</label>
                  <input
                    type="text"
                    value={formData.storageAccountName || ''}
                    onChange={(e) => handleInputChange('storageAccountName', e.target.value || null)}
                    placeholder="e.g., /storage/keystone or C:\\storage\\keystone"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Filesystem path for on-premise storage</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Account Name</label>
                  <input
                    type="text"
                    value={formData.storageAccountName || ''}
                    onChange={(e) => handleInputChange('storageAccountName', e.target.value || null)}
                    placeholder="e.g., mystorageaccount"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}
              {formData.storageProvider !== 'filesystem' && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Connection String</label>
                  <input
                    type="password"
                    value={formData.storageConnectionString || ''}
                    onChange={(e) => handleInputChange('storageConnectionString', e.target.value || null)}
                    placeholder="DefaultEndpointsProtocol=https;AccountName=..."
                    className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <strong>Note:</strong> Leave fields empty to use core storage (traceosalpynestorage). For filesystem provider, specify the local filesystem path.
            </p>
          </div>
        )}

        {activeTab === 'redis' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Host</label>
                <input
                  type="text"
                  value={formData.redisHost || ''}
                  onChange={(e) => handleInputChange('redisHost', e.target.value || null)}
                  placeholder="e.g., myredis.redis.cache.windows.net"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Port</label>
                <input
                  type="number"
                  value={formData.redisPort || 6380}
                  onChange={(e) => handleInputChange('redisPort', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Password</label>
                <input
                  type="password"
                  value={formData.redisPassword || ''}
                  onChange={(e) => handleInputChange('redisPassword', e.target.value || null)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="redisSsl"
                  checked={formData.redisSslEnabled ?? true}
                  onChange={(e) => handleInputChange('redisSslEnabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="redisSsl" className="text-sm text-gray-900 dark:text-gray-100">
                  SSL Enabled
                </label>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <strong>Note:</strong> Leave fields empty to use core Redis (traceos-redis).
            </p>
          </div>
        )}

        {activeTab === 'openai' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Provider</label>
                <select
                  value={formData.openaiProvider || 'azure_openai'}
                  onChange={(e) => handleInputChange('openaiProvider', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="azure_openai">Azure OpenAI</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">API Version</label>
                <input
                  type="text"
                  value={formData.openaiApiVersion || '2024-12-01-preview'}
                  onChange={(e) => handleInputChange('openaiApiVersion', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Endpoint</label>
                <input
                  type="text"
                  value={formData.openaiEndpoint || ''}
                  onChange={(e) => handleInputChange('openaiEndpoint', e.target.value || null)}
                  placeholder="https://myopenai.openai.azure.com/"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">API Key</label>
                <input
                  type="password"
                  value={formData.openaiApiKey || ''}
                  onChange={(e) => handleInputChange('openaiApiKey', e.target.value || null)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Embedding Deployment</label>
                <input
                  type="text"
                  value={formData.openaiEmbeddingDeployment || ''}
                  onChange={(e) => handleInputChange('openaiEmbeddingDeployment', e.target.value || null)}
                  placeholder="text-embedding-3-large"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Embedding Dimension</label>
                <input
                  type="number"
                  value={formData.openaiEmbeddingDimension || 1536}
                  onChange={(e) => handleInputChange('openaiEmbeddingDimension', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">LLM Deployment</label>
                <input
                  type="text"
                  value={formData.openaiLlmDeployment || ''}
                  onChange={(e) => handleInputChange('openaiLlmDeployment', e.target.value || null)}
                  placeholder="gpt-4"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <strong>Note:</strong> Leave fields empty to use core OpenAI (traceos-openai).
            </p>
          </div>
        )}

        {activeTab === 'azure' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500 dark:text-gray-400">
                Azure infrastructure configuration for on-premise deployments. These fields are optional but recommended for Azure resource management.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const envAzure = await loadAzureFromEnvironment();
                  setFormData(prev => ({
                    ...prev,
                    ...envAzure,
                  }));
                }}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Load from Environment
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Subscription ID</label>
                <input
                  type="text"
                  value={formData.azureSubscriptionId || ''}
                  onChange={(e) => handleInputChange('azureSubscriptionId', e.target.value || null)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Azure subscription ID for resource management</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Resource Group</label>
                <input
                  type="text"
                  value={formData.azureResourceGroup || ''}
                  onChange={(e) => handleInputChange('azureResourceGroup', e.target.value || null)}
                  placeholder="rg-myapp"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Azure resource group name</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Region</label>
                <input
                  type="text"
                  value={formData.azureRegion || ''}
                  onChange={(e) => handleInputChange('azureRegion', e.target.value || null)}
                  placeholder="canadacentral"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Azure region (e.g., canadacentral, eastus)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Key Vault Name</label>
                <input
                  type="text"
                  value={formData.azureKeyVaultName || ''}
                  onChange={(e) => handleInputChange('azureKeyVaultName', e.target.value || null)}
                  placeholder="kv-myapp"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">Azure Key Vault name for secrets management</p>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="managedIdentity"
                  checked={formData.azureManagedIdentityEnabled ?? false}
                  onChange={(e) => handleInputChange('azureManagedIdentityEnabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="managedIdentity" className="text-sm text-gray-900 dark:text-gray-100">
                  Use Managed Identity
                </label>
              </div>
            </div>
            <p className="text-sm text-slate-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <strong>Note:</strong> These Azure infrastructure fields are separate from the OpenAI configuration. 
              They're used for Azure resource management, Key Vault access, and managed identity authentication. 
              For on-premise deployments, you can load these from environment variables using the "Load from Environment" button above.
            </p>
          </div>
        )}
      </Card>

      {/* Resource Status */}
      {config && (
        <Card className="p-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Resource Status</h3>
          <div className="flex gap-6">
            {Object.entries(config.resourceStatus || {}).map(([resource, status]) => (
              <div key={resource} className="flex items-center gap-2">
                <Badge variant={status === 'custom' ? 'success' : 'default'}>
                  {status === 'custom' ? 'Custom' : 'Core'}
                </Badge>
                <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{resource}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}





